import { Edit, MusicNote, Play, Search, Tag, Tool } from '@tunasong/icons'
import type { SvgIconComponent } from '@tunasong/icons'
import type { ToolCallSummary } from '@tunasong/redux'
import type { FC } from 'react'
import { useState } from 'react'
import styles from './tool-use-chip.module.css'

const RESULT_TRUNCATE_LENGTH = 200

interface ToolVerb {
  present: string
  past: string
  Icon: SvgIconComponent
}

const defaultVerb: ToolVerb = { present: 'Using', past: 'Used', Icon: Tool }

/**
 * Tool-name → verb tense + icon mapping. Names not in the table fall back to
 * `defaultVerb`. Pattern matches (music_*, analyze_*) are handled below.
 */
const exactVerbs: Record<string, ToolVerb> = {
  search_entities: { present: 'Searching', past: 'Searched', Icon: Search },
  remember: { present: 'Remembering', past: 'Remembered', Icon: Tag },
  propose_edit: { present: 'Proposing', past: 'Proposed', Icon: Edit },
  propose_chord_change: { present: 'Proposing', past: 'Proposed', Icon: Edit },
  run_editor_command: { present: 'Running', past: 'Ran', Icon: Play },
}

const resolveVerb = (toolName: string): ToolVerb => {
  const exact = exactVerbs[toolName]
  if (exact) {
    return exact
  }
  if (toolName.startsWith('music_') || toolName.startsWith('analyze_')) {
    return { present: 'Analysing', past: 'Analysed', Icon: MusicNote }
  }
  return defaultVerb
}

const stateLabel = (state: ToolCallSummary['state']): string => {
  if (state === 'streaming') {
    return 'in progress'
  }
  if (state === 'errored') {
    return 'errored'
  }
  return 'done'
}

const formatResult = (result: unknown): string => {
  if (typeof result === 'string') {
    return result
  }
  try {
    return JSON.stringify(result)
  } catch {
    return String(result)
  }
}

export interface ToolUseChipProps {
  toolCall: ToolCallSummary
  /**
   * Optional accent color applied to the chip's border. Hosts that know the
   * agent identity at render time (e.g. `LiveAgentResponse` from `AgentChat`)
   * pass the agent's resolved color so the chip feels "owned" by the agent
   * that's running it. Unset → falls back to the neutral border in CSS.
   */
  accentColor?: string
}

/**
 * Compact chip representing a single tool call inside the live agent response.
 *
 * Visual contract (Joni's spec):
 *  - icon-left, verb + tool-name right
 *  - present-progressive verb while `state: 'streaming'`, past-tense once
 *    `called` or `errored`
 *  - colours follow `--tuna-content-secondary` (streaming) →
 *    `--tuna-content-primary` (called) → `--tuna-feedback-error` (errored)
 *  - clicking the chip toggles a disclosure region showing the streaming
 *    args (`argsText`, monospace) and the result (truncated to 200 chars,
 *    with a "Show full" affordance for the rest)
 *
 * A11y:
 *  - the chip is a `<button>` with `aria-expanded` reflecting disclosure
 *  - `aria-label` carries the verb, tool name, and state so screen readers
 *    don't need to read the icon glyph (which is `aria-hidden`)
 */
export const ToolUseChip: FC<ToolUseChipProps> = props => {
  const { toolCall, accentColor } = props
  const { name, state, argsText, result } = toolCall

  const [expanded, setExpanded] = useState(false)
  const [showFullResult, setShowFullResult] = useState(false)

  const verb = resolveVerb(name)
  const verbWord = state === 'streaming' ? verb.present : verb.past
  const Icon = verb.Icon

  const ariaLabel = `${verbWord} ${name}: ${stateLabel(state)}`

  const handleToggle = () => setExpanded(prev => !prev)
  const handleShowFull = () => setShowFullResult(true)

  const formattedResult = result !== undefined ? formatResult(result) : null
  const resultIsTruncated = formattedResult !== null && formattedResult.length > RESULT_TRUNCATE_LENGTH
  const visibleResult =
    formattedResult === null
      ? null
      : !resultIsTruncated || showFullResult
        ? formattedResult
        : `${formattedResult.slice(0, RESULT_TRUNCATE_LENGTH)}…`

  return (
    <>
      <button
        type="button"
        className={styles.chip}
        data-state={state}
        aria-expanded={expanded}
        aria-label={ariaLabel}
        onClick={handleToggle}
        style={accentColor && state !== 'errored' ? { borderColor: accentColor } : undefined}
      >
        <Icon className={styles.icon} aria-hidden="true" />
        <span className={styles.verb}>{verbWord}</span>
        <span className={styles.toolName}>{name}</span>
      </button>

      {expanded && (argsText || formattedResult !== null) ? (
        <div className={styles.disclosure} role="region" aria-label={`${name} details`}>
          {argsText ? (
            <>
              <span className={styles.disclosureLabel}>Arguments</span>
              <pre className={styles.disclosureBody}>{argsText}</pre>
            </>
          ) : null}
          {visibleResult !== null ? (
            <>
              <span className={styles.disclosureLabel}>Result</span>
              <pre className={styles.disclosureBody}>{visibleResult}</pre>
              {resultIsTruncated && !showFullResult ? (
                <button type="button" className={styles.showFull} onClick={handleShowFull}>
                  Show full
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </>
  )
}
